import axios from 'axios'

import type { BaseQueryFn } from '@reduxjs/toolkit/query/react'
import type { AxiosError } from 'axios'

import type { APIErrors, BadServiceInputError, EntityNotFoundError, EntityUniqueConstraintBrokenError, InternalServerError } from './errors'

interface AxiosAPIError {
  status: number
  data: { code: string }
}

/** Skip undefined values so RTK Query caches stable param keys. */
export const definedParams = (entries: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(entries)) {
    if (value !== undefined) {
      out[key] = value
    }
  }
  return out
}

export const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: '' }
  ): BaseQueryFn<
    {
      url: string
      method: string
      data?: unknown
      params?: unknown
    },
    unknown,
    APIErrors
  > =>
  async ({ url, method, data, params }) => {
    try {
      const result = await axios({ url: baseUrl + url, method, data, params })

      return { data: result.data }
    } catch (error) {
      const { status, data } = ((error as AxiosError).response as AxiosAPIError) ?? {
        status: 500,
        data: {
          code: 'internal-server-error',
          message: 'Server has not been able to fulfill the current request',
          meta: {},
        },
      }

      if (status === 400 && data.code === 'bad-service-input') {
        return { error: data as BadServiceInputError }
      } else if (status === 404 && data.code === 'entity-not-found') {
        return { error: data as EntityNotFoundError }
      } else if (status === 422 && data.code === 'entity-unique-constraint-broken') {
        return { error: data as EntityUniqueConstraintBrokenError }
      } else {
        return { error: data as InternalServerError }
      }
    }
  }
